import React, { ReactNode, useMemo, useState } from 'react';
import classNames from 'classnames';
import { SORTING_ORDER } from 'models/Sort';
import { useTranslation } from 'react-i18next';
import { NDATemplate } from 'models/Nda';
import { getIsDesc, getSortComparator, includesIgnoreCase } from 'utils';
import { Input, Loader } from 'ncoded-component-library';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import RadioButtonField from 'components/RadioGroupField/RadioButtonField';
import { Field } from 'react-final-form';
import validations from 'validations';

import './NdaOptions.styles.scss';

type NdaOptionsProps = {
  className?: string;
  isLoading?: boolean;
  canSelect?: boolean;
  templates: NDATemplate[];
  getActions: (templateId: string) => ReactNode;
};

const NdaOptions: React.FC<NdaOptionsProps> = (props) => {
  const { className, children, isLoading, canSelect, templates, getActions } =
    props;

  const [sort, setSort] = useState<string>(SORTING_ORDER.A_TO_Z);
  const [search, setSearch] = useState('');

  const { t } = useTranslation();

  const trimmedSearch = useMemo(() => search.trim(), [search]);

  const activeTemplates = useMemo(
    () =>
      templates.filter(
        ({ name }) =>
          name && (!trimmedSearch || includesIgnoreCase(name, trimmedSearch)),
      ),
    [templates, trimmedSearch],
  );

  const templateOptions = useMemo(
    () =>
      activeTemplates
        .sort((a, b) => getSortComparator(a.name, b.name, getIsDesc(sort)))
        .map(({ templateId, name }) => ({
          value: templateId,
          label: name,
          actions: (
            <div className="nda-options__actions">{getActions(templateId)}</div>
          ),
        })),
    [activeTemplates, sort, getActions],
  );

  const classes = classNames('nda-options', className);

  return (
    <div className={classes}>
      <div className="sort">
        <p>{t('name')}</p>
        <Input
          value={search}
          disabled={isLoading}
          onChange={({ target }) => setSearch(target.value)}
        />
        <SortingButton
          content={t('aToZ')}
          sortingValue={SORTING_ORDER.A_TO_Z}
          sort={sort}
          disabled={isLoading}
          updateSort={setSort}
        />

        {children}
      </div>

      <div className="options-wrapper">
        {isLoading && <Loader />}

        {!isLoading &&
          (templateOptions.length ? (
            templateOptions.map(({ value, label, actions }) => (
              <div key={value}>
                {canSelect ? (
                  <Field
                    name="templateId"
                    type="radio"
                    label={label}
                    value={value}
                    component={RadioButtonField}
                    required
                    validate={validations.required()}
                  />
                ) : (
                  label
                )}
                {actions}
              </div>
            ))
          ) : (
            <p className="no-ndas">
              {t(trimmedSearch ? 'noNdasForSearched' : 'noNdas', {
                search,
              })}
            </p>
          ))}
      </div>
    </div>
  );
};

export default NdaOptions;
